import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ICreateShopObject } from 'models/shop/createshop';
import { IStore } from 'redux/IStore';
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import { Button, ErrorSummary } from 'components';
import { PortalPageSection } from 'components/PortalStructure';
import { createCountriesOptions, createDomainValueOptions } from 'helpers/common';
import { InjectedRouter } from 'react-router';
import { BookingInputField, BookingSelectMenu } from 'components/FormControls';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { IManageShop } from 'models/shop/manageshop';
import { IShopProfile } from 'models/shop/shopprofile';
import { clearFormData, resetFormData } from 'redux/modules/shop/manageshop';
import { required, maxLength } from 'helpers/validationattributes';
import { getCountryDomainValues, getShopTypeDomainValues } from 'redux/modules/domainvalues';
import { preventRouterLeave } from 'helpers/hooks';
import { Dispatch } from 'react-redux';
import { PortalSwitchField } from 'components/FormControls';
import ShopSmsReport from '../ShopSmsReport';
const { connect } = require('react-redux');

interface IOwnProps {
	redux: 'createShop' | 'manageShop';

	onSubmit?: ((form: ICreateShopObject) => void) | ((form: ICreateShopObject<IShopProfile>) => void);
	previousPage?: () => void;
}

interface IProps
	extends IOwnProps,
		ITranslateProps,
		InjectedFormProps<ICreateShopObject | IManageShop>,
		ReturnType<typeof stateToProps>,
		ReturnType<typeof dispatchToProps> {
	location?: any;
	userEmail?: string;
	router: InjectedRouter;
	route: any;
}

interface IState {
	selectedPayment: 'online-payment' | 'onsite-payment' | 'both-payments';
}

const maxLength2 = maxLength(2);
@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopGeneralForm extends React.Component<IProps, IState> {
	deleteImage: any;

	constructor(props) {
		super(props);

		this.state = {
			selectedPayment: null,
		};

		this.onSubmit = this.onSubmit.bind(this);
		this.onPaymentTypeChange = this.onPaymentTypeChange.bind(this);
	}

	public componentDidMount() {
		this.props.getCountryDomainValues();
		this.props.getShopTypeDomainValues();
	}

	public componentDidUpdate(): void {
		if (!this.state.selectedPayment && this.props.currentFormValues) {
			const formShop = this.props.currentFormValues.shop;
			if (formShop.allowUnpaidOrders && formShop.isOnlinePaymentEnabled) {
				this.setState({ selectedPayment: 'both-payments' });
			} else if (!formShop.allowUnpaidOrders && formShop.isOnlinePaymentEnabled) {
				this.setState({ selectedPayment: 'online-payment' });
			} else if (formShop.allowUnpaidOrders && !formShop.isOnlinePaymentEnabled) {
				this.setState({ selectedPayment: 'onsite-payment' });
			}
		}
	}

	public onSubmit(form: ICreateShopObject) {
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	private onPaymentTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
		const tempSelectedPayment = event.target.value as 'both-payments' | 'online-payment' | 'onsite-payment';

		if (!tempSelectedPayment) {
			return;
		}

		if (tempSelectedPayment === 'both-payments') {
			this.props.change('shop.isOnlinePaymentEnabled', true);
			this.props.change('shop.allowUnpaidOrders', true);
		} else if (tempSelectedPayment === 'online-payment') {
			this.props.change('shop.isOnlinePaymentEnabled', true);
			this.props.change('shop.allowUnpaidOrders', false);
		} else if (tempSelectedPayment === 'onsite-payment') {
			this.props.change('shop.isOnlinePaymentEnabled', false);
			this.props.change('shop.allowUnpaidOrders', true);
		}

		this.setState({ selectedPayment: tempSelectedPayment });
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'name':
				return t('Shop_ManageShop_General_Label_SalonName');
			case 'shopTypes':
				return t('Shop_ManageShop_General_Label_ShopType');
			case 'code':
				return t('Shop_ManageShop_General_Label_ShopHandle');
			case 'fromCountryKey':
				return t('Cart_CheckoutAddress_Label_Country');
			case 'description':
				return t('Shop_ManageShop_General_Label_Description');
			case 'about':
				return t('Shop_ManageShop_General_Label_About');
			case 'email':
				return t('BSPortal_Common_EmailAddress');
			case 'phone':
				return t('Shop_ManageShop_ShippingAddress_Label_Phone');
			default:
				return key;
		}
	}

	private renderEditableInfo() {
		const { t, countryDomainValues, shopTypeDomainValues, userPreferences, currentFormValues } = this.props;
		const countries = createCountriesOptions(t, countryDomainValues);
		const shopTypes = createDomainValueOptions(t, 'DV_ShopTypes_', shopTypeDomainValues);
		const shopIntervalItems = [
			{ key: 600, value: '10 min' },
			{ key: 900, value: '15 min' },
			{ key: 1200, value: '20 min' },
			{ key: 1800, value: '30 min' },
			{ key: 2700, value: '45 min' },
			{ key: 3600, value: '60 min' },
		];

		return (
			<>
				<Field
					name="shop.name"
					component={BookingInputField}
					type="text"
					validate={[required]}
					label={t('Shop_ManageShop_General_Label_SalonName')}
					information={t('Shop_ManageShop_General_Label_SalonName')}
				/>
				<div className="bookingSelectMenuWrapper">
					<Field
						name="shop.shopTypes"
						component={BookingSelectMenu}
						type="text"
						items={shopTypes}
						multi={true}
						validate={[required]}
						isRequired={true}
						label={t('Shop_ManageShop_General_Label_ShopType')}
						information={t('Shop_ManageShop_General_Label_ShopType')}
						searchable={false}
					/>
				</div>
				<div className="bookingSelectMenuWrapper">
					<Field
						name={'shop.shippingFromCountryKey'}
						component={BookingSelectMenu}
						items={countries}
						validate={[required, maxLength2]}
						isRequired={true}
						multi={false}
						closeOnSelect={true}
						label={t('Cart_CheckoutAddress_Label_Country')}
						information={t('Cart_CheckoutAddress_Label_Country')}
					/>
				</div>
				<Field
					name={'shop.email'}
					isDisabled={true}
					readOnly={true}
					type="text"
					label={t('BSPortal_Common_EmailAddress')}
					information={t('BSPortal_Common_EmailAddress')}
					input={{ value: currentFormValues?.shop?.shippingEmail || userPreferences?.email }}
					component={BookingInputField}
				/>
				<Field
					name={'shop.ownerData.name'}
					isDisabled={true}
					readOnly={true}
					type="text"
					label={t('Shop_ManageShop_Members_Label_Owner')}
					information={t('Shop_ManageShop_Members_Label_Owner')}
					input={{ value: currentFormValues?.shop?.ownerData?.name }}
					component={BookingInputField}
				/>
				<div className="bookingSelectMenuWrapper">
					<Field
						name="shop.bookingInterval"
						component={BookingSelectMenu}
						items={shopIntervalItems}
						validate={[required]}
						isRequired={true}
						multi={false}
						closeOnSelect={true}
						label={t('Shop_ManageShop_General_Label_BookingInterval')}
						information={t('Shop_ManageShop_General_Label_BookingInterval')}
						searchable={false}
					/>
				</div>
				<Field
					name="shop.description"
					component={BookingInputField}
					type="text"
					validate={[required]}
					label={t('Shop_ManageShop_General_Label_ShortSalonDescription')}
					information={t('Shop_ManageShop_General_Label_ShortSalonDescription')}
				/>
			</>
		);
	}

	private renderEditableSalonNotifications() {
		const { t } = this.props;

		return (
			<>
				<h2 className="shopGeneral__subtitle first">{t('User_ShopCard_Salon')}</h2>

				<Field
					name="shop.isEmailNotificationEnabled"
					component={PortalSwitchField}
					type="checkbox"
					label={t('Shop_ShopCustomer_ReceiveEmailNotifications')}
				/>
			</>
		);
	}

	private renderEditableCustomerNotifications() {
		const { t } = this.props;

		return (
			<>
				<Field
					name="shop.isSendSMSNotificationEnabled"
					component={PortalSwitchField}
					type="checkbox"
					label={t('Shop_ShopCustomer_SendSMSNotifications')}
				/>

				<ShopSmsReport shop={this.props?.currentFormValues?.shop} t={t} />
			</>
		);
	}

	private renderEditablePayments() {
		const { t } = this.props;
		const radioButtonValues = [
			{ text: t('Shop_ManageShop_General_Label_OnlineAndOnsitePayment'), key: 'both-payments' },
			{ text: t('Shop_ManageShop_General_Label_OnlyOnlinePayments'), key: 'online-payment' },
			{ text: t('Shop_ManageShop_General_Label_OnlyOnsitePayments'), key: 'onsite-payment' },
		];

		return (
			<>
				<p className="shopGeneral__paymentOptionsText">{t('Shop_ManageShop_General_Label_ChoosePaymentOption')}</p>

				{radioButtonValues.map((item) => {
					return (
						<div key={`paymentOptionContainer` + item.key} className="shopGeneral__paymentOptionContainer">
							<label className="shopGeneral__paymentOptionButton">
								<input
									key={item.key}
									name={item.text}
									value={item.key}
									type="radio"
									checked={item.key === this.state.selectedPayment}
									onChange={(e) => this.onPaymentTypeChange(e)}
								/>
								<span className="shopGeneral__paymentOptionText">{item.text}</span>
							</label>
						</div>
					);
				})}
			</>
		);
	}

	public render() {
		const { handleSubmit, formErrors, submitFailed, t, location, pristine, submitting } = this.props;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.createShop ? this.props.createShop.error : false,
			message: this.props.createShop ? this.props.createShop.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		return (
			<>
				<form onSubmit={handleSubmit(this.onSubmit)}>
					<div className="profileError">
						<ErrorSummary {...errorSummaryProps} />
					</div>
					<PortalPageSection title={t('BSPortal_Common_Info')} className="shopGeneral__card">
						{this.renderEditableInfo()}
					</PortalPageSection>

					<PortalPageSection title={t('Shop_ManageShop_General_Notifications')} className="shopGeneral__card">
						{this.renderEditableSalonNotifications()}
					</PortalPageSection>

					<PortalPageSection
						title={t('Shop_ManageShop_General_Title_RemindersForClients')}
						className="shopGeneral__card"
					>
						{this.renderEditableCustomerNotifications()}
					</PortalPageSection>

					<PortalPageSection title={t('Shop_ManageShop_General_Title_Payments')} className="shopGeneral__card">
						{this.renderEditablePayments()}
					</PortalPageSection>

					{!pristine && (
						<div className="shopGeneral__bottomContainer">
							<div className="container">
								<div className="row">
									<div className="col-sm-9 col-md-10 col-12 offset-sm-3 offset-md-2">
										<div className="shopGeneral__bottomButtons">
											<div className="shopGeneral__cancelButton" onClick={() => this.props.resetFormData()}>
												{t('BSPortal_Common_Cancel')}
											</div>
											<Button className="shopGeneral__submitButton" type="submit" disabled={pristine || submitting}>
												{t('BSPortal_Common_Save')}
											</Button>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</form>
			</>
		);
	}
}

const cachedComponents: { [key: string]: ReturnType<typeof createConnected> } = {};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getCountryDomainValues: () => dispatch(getCountryDomainValues()),
	getShopTypeDomainValues: () => dispatch(getShopTypeDomainValues()),
	clearFormData: () => dispatch(clearFormData()),
	resetFormData: () => dispatch(resetFormData()),
});

function stateToProps(
	formName: typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP,
	state: IStore,
	ownProps: IOwnProps,
) {
	let currentFormValues: ICreateShopObject | IManageShop = null;
	const shopObj =
		ownProps.redux === 'createShop'
			? state[ownProps.redux].createShopObj
			: !state.manageShop.isFetching
			  ? state[ownProps.redux]
			  : state.form[formName].values; // if fetching manageShop use formValues (it resets to original data until it is done saving otherwise)
	if (state.form[formName] && state.form[formName].values) {
		currentFormValues = state.form[formName].values;
	} else {
		currentFormValues = shopObj;
	}

	return {
		createShop: state[ownProps.redux],
		initialValues: shopObj as Partial<ICreateShopObject | IManageShop>,
		currentFormValues,
		formErrors: getFormSyncErrors(formName)(state),
		countryDomainValues: state.domainValues.countries,
		shopTypeDomainValues: state.domainValues.shopType,
		fetchDefault: state.fetchDefault,
		userPreferences: state.userPreferences,
	};
}

function createConnected(formName: typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP) {
	let ShopGeneral = reduxForm({
		form: formName,
		destroyOnUnmount: formName === c.FORM_NAME_MANAGE_SHOP,
		forceUnregisterOnUnmount: true,
		enableReinitialize: formName === c.FORM_NAME_MANAGE_SHOP,
	})(ShopGeneralForm) as React.FC<IOwnProps>;

	ShopGeneral = connect(
		(state: IStore, ownProps: IOwnProps) => stateToProps(formName, state, ownProps),
		dispatchToProps,
	)(ShopGeneral);

	return ShopGeneral;
}

export function getConnectedShopGeneral(formName: typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP) {
	if (!(formName in cachedComponents)) {
		cachedComponents[formName] = createConnected(formName);
	}
	return cachedComponents[formName];
}

export const ShopGeneral = getConnectedShopGeneral(c.FORM_NAME_MANAGE_SHOP);
